/**
 * Terra Wearables Service
 *
 * Integrates with Terra API (https://tryterra.co) to fetch wearable device data
 * including activity, sleep, heart rate, and body metrics from devices like:
 * - Garmin, Fitbit, Apple Watch, Whoop, Oura, Polar, etc.
 *
 * Phase 3 - Advanced Integration
 */

import { HTTP } from "./http";

let API_SERVER = "";
if (window.location.href.indexOf("8080") > -1) {
  API_SERVER = "http://www.localhost:8888/back-end-endpoint-terra.php";
} else {
  API_SERVER = "/personal-training-BE/back-end-endpoint-terra.php";
}

const TerraWearables = {
  // =========================================================================
  // USER AUTHENTICATION & CONNECTION
  // =========================================================================

  /**
   * Generate widget session for user to connect their wearable
   * This creates a Terra widget URL for the user to authenticate their device
   * @param {number} userId - Internal user ID
   * @param {string} redirectUrl - URL to redirect after authentication
   */
  generateWidgetSession(userId, redirectUrl = null) {
    return HTTP.post(API_SERVER, {
      action: "generateWidgetSession",
      user_id: userId,
      redirect_url: redirectUrl,
    });
  },

  /**
   * Get user's connected wearable devices
   * @param {number} userId - User ID
   */
  getConnectedDevices(userId) {
    return HTTP.post(API_SERVER, {
      action: "getConnectedDevices",
      user_id: userId,
    });
  },

  /**
   * Disconnect a wearable device
   * @param {number} userId - User ID
   * @param {string} terraUserId - Terra user ID for the device
   */
  disconnectDevice(userId, terraUserId) {
    return HTTP.post(API_SERVER, {
      action: "disconnectDevice",
      user_id: userId,
      terra_user_id: terraUserId,
    });
  },

  /**
   * Check connection status for a user
   * @param {number} userId - User ID
   */
  checkConnectionStatus(userId) {
    return HTTP.post(API_SERVER, {
      action: "checkConnectionStatus",
      user_id: userId,
    });
  },

  // =========================================================================
  // ACTIVITY DATA
  // =========================================================================

  /**
   * Get activity data (steps, distance, calories burned, etc.)
   * @param {number} userId - User ID
   * @param {string} startDate - Start date YYYY-MM-DD
   * @param {string} endDate - End date YYYY-MM-DD
   */
  getActivityData(userId, startDate, endDate = null) {
    return HTTP.post(API_SERVER, {
      action: "getActivityData",
      user_id: userId,
      start_date: startDate,
      end_date: endDate || startDate,
    });
  },

  /**
   * Get today's activity summary
   * @param {number} userId - User ID
   */
  getTodayActivity(userId) {
    const today = new Date().toISOString().split("T")[0];
    return this.getActivityData(userId, today, today);
  },

  /**
   * Get workout/exercise data
   * @param {number} userId - User ID
   * @param {string} startDate - Start date YYYY-MM-DD
   * @param {string} endDate - End date YYYY-MM-DD
   */
  getWorkoutData(userId, startDate, endDate = null) {
    return HTTP.post(API_SERVER, {
      action: "getWorkoutData",
      user_id: userId,
      start_date: startDate,
      end_date: endDate || startDate,
    });
  },

  // =========================================================================
  // BODY METRICS
  // =========================================================================

  /**
   * Get body metrics (weight, body fat, etc.)
   * @param {number} userId - User ID
   * @param {string} startDate - Start date YYYY-MM-DD
   * @param {string} endDate - End date YYYY-MM-DD
   */
  getBodyData(userId, startDate, endDate = null) {
    return HTTP.post(API_SERVER, {
      action: "getBodyData",
      user_id: userId,
      start_date: startDate,
      end_date: endDate || startDate,
    });
  },

  /**
   * Get heart rate data
   * @param {number} userId - User ID
   * @param {string} startDate - Start date YYYY-MM-DD
   * @param {string} endDate - End date YYYY-MM-DD
   */
  getHeartRateData(userId, startDate, endDate = null) {
    return HTTP.post(API_SERVER, {
      action: "getHeartRateData",
      user_id: userId,
      start_date: startDate,
      end_date: endDate || startDate,
    });
  },

  // =========================================================================
  // SLEEP DATA
  // =========================================================================

  /**
   * Get sleep data
   * @param {number} userId - User ID
   * @param {string} startDate - Start date YYYY-MM-DD
   * @param {string} endDate - End date YYYY-MM-DD
   */
  getSleepData(userId, startDate, endDate = null) {
    return HTTP.post(API_SERVER, {
      action: "getSleepData",
      user_id: userId,
      start_date: startDate,
      end_date: endDate || startDate,
    });
  },

  // =========================================================================
  // NUTRITION INTEGRATION
  // =========================================================================

  /**
   * Calculate recommended caloric intake based on activity data and training load
   * @param {number} userId - User ID
   * @param {string} date - Date YYYY-MM-DD
   * @param {Object} options - Additional options
   * @param {number} options.weight - Player weight in kg
   * @param {string} options.activityContext - Day context (match_day, training, rest, etc.)
   * @param {number} options.trainingLoad - Training load from GPS data (optional)
   */
  calculateCaloricNeeds(userId, date, options = {}) {
    return HTTP.post(API_SERVER, {
      action: "calculateCaloricNeeds",
      user_id: userId,
      date: date,
      weight: options.weight || null,
      activity_context: options.activityContext || null,
      training_load: options.trainingLoad || null,
    });
  },

  /**
   * Get comprehensive daily summary combining all wearable data
   * @param {number} userId - User ID
   * @param {string} date - Date YYYY-MM-DD
   */
  getDailySummary(userId, date = null) {
    const targetDate = date || new Date().toISOString().split("T")[0];
    return HTTP.post(API_SERVER, {
      action: "getDailySummary",
      user_id: userId,
      date: targetDate,
    });
  },

  // =========================================================================
  // TEAM-WIDE DATA (Admin/Coach)
  // =========================================================================

  /**
   * Get team wearable connections overview
   * @param {number} clubId - Club ID
   * @param {number} teamId - Team ID (optional)
   */
  getTeamConnections(clubId, teamId = null) {
    return HTTP.post(API_SERVER, {
      action: "getTeamConnections",
      club_id: clubId,
      team_id: teamId,
    });
  },

  /**
   * Get team activity dashboard
   * @param {number} clubId - Club ID
   * @param {number} teamId - Team ID (optional)
   * @param {string} date - Date YYYY-MM-DD
   */
  getTeamActivityDashboard(clubId, teamId = null, date = null) {
    return HTTP.post(API_SERVER, {
      action: "getTeamActivityDashboard",
      club_id: clubId,
      team_id: teamId,
      date: date || new Date().toISOString().split("T")[0],
    });
  },

  /**
   * Get team sleep quality overview
   * @param {number} clubId - Club ID
   * @param {number} teamId - Team ID (optional)
   * @param {string} date - Date YYYY-MM-DD
   */
  getTeamSleepOverview(clubId, teamId = null, date = null) {
    return HTTP.post(API_SERVER, {
      action: "getTeamSleepOverview",
      club_id: clubId,
      team_id: teamId,
      date: date || new Date().toISOString().split("T")[0],
    });
  },

  // =========================================================================
  // UTILITY METHODS
  // =========================================================================

  /**
   * Get supported wearable providers
   */
  getSupportedProviders() {
    return [
      { id: "GARMIN", name: "Garmin", icon: "garmin", color: "#007CC3" },
      { id: "FITBIT", name: "Fitbit", icon: "fitbit", color: "#00B0B9" },
      { id: "APPLE", name: "Apple Health", icon: "apple", color: "#000000" },
      { id: "WHOOP", name: "Whoop", icon: "whoop", color: "#00A5E0" },
      { id: "OURA", name: "Oura Ring", icon: "oura", color: "#C7C7C7" },
      { id: "POLAR", name: "Polar", icon: "polar", color: "#D10000" },
      { id: "SUUNTO", name: "Suunto", icon: "suunto", color: "#000000" },
      { id: "COROS", name: "Coros", icon: "coros", color: "#FF6B00" },
      { id: "SAMSUNG", name: "Samsung Health", icon: "samsung", color: "#1428A0" },
      { id: "GOOGLE", name: "Google Fit", icon: "google", color: "#4285F4" },
    ];
  },

  /**
   * Get activity type display info
   * @param {string} activityType - Activity type from Terra
   */
  getActivityTypeInfo(activityType) {
    const types = {
      running: { label: "Running", icon: "R", color: "#28a745" },
      cycling: { label: "Cycling", icon: "C", color: "#17a2b8" },
      swimming: { label: "Swimming", icon: "S", color: "#007bff" },
      walking: { label: "Walking", icon: "W", color: "#6c757d" },
      strength_training: { label: "Strength", icon: "ST", color: "#fd7e14" },
      soccer: { label: "Football", icon: "F", color: "#28a745" },
      hiit: { label: "HIIT", icon: "H", color: "#dc3545" },
      yoga: { label: "Yoga", icon: "Y", color: "#6f42c1" },
      other: { label: "Other", icon: "O", color: "#6c757d" },
    };
    return types[activityType?.toLowerCase()] || types.other;
  },

  /**
   * Get sleep quality label
   * @param {number} score - Sleep score (0-100)
   */
  getSleepQualityLabel(score) {
    if (score >= 85) return { label: "Excellent", color: "#28a745" };
    if (score >= 70) return { label: "Good", color: "#17a2b8" };
    if (score >= 50) return { label: "Fair", color: "#ffc107" };
    return { label: "Poor", color: "#dc3545" };
  },

  /**
   * Format duration from seconds to readable string
   * @param {number} seconds - Duration in seconds
   */
  formatDuration(seconds) {
    if (!seconds) return "0m";
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  },

  /**
   * Calculate training load from wearable metrics
   * @param {Object} activityData - Activity data from Terra
   * @returns {Object} Training load metrics
   */
  calculateTrainingLoad(activityData) {
    // Simple training load calculation based on duration, intensity, and heart rate
    const duration = activityData.active_duration_seconds || 0;
    const avgHR = activityData.avg_heart_rate || 120;
    const maxHR = activityData.max_heart_rate || 180;
    const calories = activityData.calories || 0;

    // Training Impulse (TRIMP) simplified calculation
    const hrReserve = (avgHR - 60) / (maxHR - 60);
    const trimp = (duration / 60) * hrReserve;

    // Estimated training load score (0-100+)
    const loadScore = Math.min(100, Math.round((trimp / 3) + (calories / 50)));

    return {
      trimp: Math.round(trimp),
      loadScore,
      intensity: hrReserve > 0.8 ? "High" : hrReserve > 0.6 ? "Medium" : "Low",
      intensityColor: hrReserve > 0.8 ? "#dc3545" : hrReserve > 0.6 ? "#ffc107" : "#28a745",
    };
  },
};

export { TerraWearables };
export default TerraWearables;
